// Pricing migration 32 (ADO-3985), T9: the shared multi-buy deal lookup.
//
// One Zustand store (ADR-004) every product surface reads its deals from, keyed per SKU. Pages
// ask for the deals of the products they show through `useMultibuyDeals`, once per page beside
// `usePrices`; cards read their own SKU's entry.
//
// Mirrors the price store (`pricing/usePriceStore.ts`): same-tick batching on a microtask,
// in-flight coalescing, and a generation bumped whenever the key (`priceKey`: the ordering entry
// and the trading account) changes, so an answer for a key we left is never written. No TTL and
// no persistence. Deals are shown only with `VITE_REAL_PRICES_ENABLED` on (D-5); with it off, or
// with no ordering entry, nothing is requested.
//
// The re-ask after a basket write (`invalidate`, D-2) comes with the dialog's steppers (story
// ADO-3985, T13).

import { useEffect } from 'react';
import { create } from 'zustand';
import { fetchMultibuyDeals, type MultibuyDeal } from '../api/multibuy-deals.client';
import { useSessionStore } from '../auth/useSessionStore';
import { realPricesEnabled } from '../pricing/realPrices';
import {
  priceKey,
  useCurrentPriceKey,
  usePriceAccountCode,
  type ReadOutcome,
} from '../pricing/usePriceStore';

/** One SKU's deal state. A SKU with no entry has not been asked for yet, and shows no deal. */
export type MultibuyDealsEntry =
  | { readonly state: 'loading' }
  /** Answered: every live deal reaching the product, none being an empty list. */
  | { readonly state: 'ready'; readonly deals: readonly MultibuyDeal[] }
  /** Nobody answered: the request failed, or the answer for this SKU was unusable. */
  | { readonly state: 'failed' };

interface BatchOutcome {
  readonly unread: ReadonlySet<string>;
  readonly superseded: ReadonlySet<string>;
}

interface MultibuyDealsStoreState {
  /** The entry and account the entries belong to (`priceKey`), or `null` before any load. */
  key: string | null;
  entries: Readonly<Record<string, MultibuyDealsEntry>>;
  /** Load the deals of `skuCodes` under `entryKey` for `accountCode`; resolves to this caller's
   *  `ReadOutcome` once the read settles. */
  loadForSkus: (
    entryKey: string,
    accountCode: string | null,
    skuCodes: readonly string[],
  ) => Promise<ReadOutcome>;
  /** Drop everything (tests, session hygiene). */
  reset: () => void;
}

/** Bumped on every key change and reset: a batch writes only for the generation it was opened
 *  in. */
let generation = 0;
/** SKU to the promise of the batch currently fetching it, for the current generation. */
let inFlightReads: Record<string, Promise<BatchOutcome>> = {};

interface PendingBatch {
  readonly entryKey: string;
  readonly accountCode: string | null;
  readonly generation: number;
  readonly skus: Set<string>;
  readonly promise: Promise<BatchOutcome>;
  readonly settle: (outcome: BatchOutcome) => void;
}
let pendingBatch: PendingBatch | null = null;

function dropEverything(): void {
  generation += 1;
  inFlightReads = {};
  pendingBatch = null;
}

export const useMultibuyDealsStore = create<MultibuyDealsStoreState>((set, get) => {
  /** Send one accumulated batch. Always settles its promise and clears its in-flight entries. */
  async function flushBatch(batch: PendingBatch): Promise<void> {
    if (pendingBatch === batch) pendingBatch = null;
    const toFetch = [...batch.skus];
    let unread: ReadonlySet<string> = new Set(toFetch);
    let superseded: ReadonlySet<string> = new Set<string>();
    try {
      if (batch.generation !== generation) {
        superseded = new Set(toFetch);
        unread = new Set<string>();
        return;
      }
      const { answers, unreadSkus } = await fetchMultibuyDeals(
        batch.entryKey,
        batch.accountCode,
        toFetch,
      );
      if (batch.generation !== generation) {
        superseded = new Set(toFetch);
        unread = new Set<string>();
        return;
      }
      unread = new Set(unreadSkus);
      const next: Record<string, MultibuyDealsEntry> = { ...get().entries };
      for (const answer of answers) next[answer.skuCode] = { state: 'ready', deals: answer.deals };
      for (const sku of unreadSkus) next[sku] = { state: 'failed' };
      set({ entries: next });
    } catch {
      // `fetchMultibuyDeals` never rejects, so reaching here means something unexpected did.
      if (batch.generation === generation) {
        const next: Record<string, MultibuyDealsEntry> = { ...get().entries };
        for (const sku of toFetch) {
          if (next[sku]?.state === 'loading') next[sku] = { state: 'failed' };
        }
        set({ entries: next });
      }
      unread = new Set(toFetch);
      superseded = new Set<string>();
    } finally {
      for (const sku of toFetch) {
        if (inFlightReads[sku] === batch.promise) delete inFlightReads[sku];
      }
      batch.settle({ unread, superseded });
    }
  }

  /** Add `skus` to the tick's open batch (opening one if needed) and return its promise. */
  function enqueueRead(
    entryKey: string,
    accountCode: string | null,
    skus: readonly string[],
  ): Promise<BatchOutcome> {
    if (pendingBatch === null || pendingBatch.generation !== generation) {
      let settle!: (outcome: BatchOutcome) => void;
      const promise = new Promise<BatchOutcome>((resolve) => {
        settle = resolve;
      });
      const batch: PendingBatch = {
        entryKey,
        accountCode,
        generation,
        skus: new Set(),
        promise,
        settle,
      };
      pendingBatch = batch;
      queueMicrotask(() => void flushBatch(batch));
    }
    const batch = pendingBatch;
    for (const sku of skus) {
      batch.skus.add(sku);
      inFlightReads[sku] = batch.promise;
    }
    return batch.promise;
  }

  /** The caller's outcome over the batches its SKUs rode. */
  async function outcomeOf(
    skus: readonly string[],
    riding: ReadonlySet<Promise<BatchOutcome>>,
  ): Promise<ReadOutcome> {
    const outcomes = await Promise.all([...riding]);
    if (skus.some((sku) => outcomes.some((outcome) => outcome.unread.has(sku)))) return 'unread';
    if (skus.some((sku) => outcomes.some((outcome) => outcome.superseded.has(sku))))
      return 'superseded';
    return 'answered';
  }

  return {
    key: null,
    entries: {},

    async loadForSkus(entryKey, accountCode, skuCodes) {
      if (!realPricesEnabled()) return 'answered'; // D-5: the flag is off, never ask
      const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
      if (unique.length === 0) return 'answered';

      const key = priceKey(entryKey, accountCode);
      let entries = get().entries;
      if (get().key !== key) {
        dropEverything();
        entries = {};
      }
      const loading = unique.filter((sku) => entries[sku] === undefined);
      if (loading.length > 0 || get().key !== key) {
        const next: Record<string, MultibuyDealsEntry> = { ...entries };
        for (const sku of loading) next[sku] = { state: 'loading' };
        set({ key, entries: next });
      }

      const riding = new Set<Promise<BatchOutcome>>();
      const toFetch: string[] = [];
      for (const sku of unique) {
        const existing = inFlightReads[sku];
        if (existing !== undefined) riding.add(existing);
        else toFetch.push(sku);
      }
      if (toFetch.length > 0) riding.add(enqueueRead(entryKey, accountCode, toFetch));
      return outcomeOf(unique, riding);
    },

    reset() {
      dropEverything();
      set({ key: null, entries: {} });
    },
  };
});

/**
 * Ask for the deals of the products a page or list shows, in one request. Call it once per page,
 * beside `usePrices`, with the same SKUs. Re-asks when the SKU set, the ordering entry or the
 * trading account changes. A no-op with the flag off or with no ordering entry chosen.
 */
export function useMultibuyDeals(skuCodes: readonly string[]): void {
  // Keyed on the VALUE of the SKU list: pages rebuild the array on every render.
  const skuKey = [...new Set(skuCodes)].sort().join(',');
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const accountCode = usePriceAccountCode();

  useEffect(() => {
    if (!realPricesEnabled() || entryKey === null || skuKey === '') return;
    void useMultibuyDealsStore.getState().loadForSkus(entryKey, accountCode, skuKey.split(','));
  }, [skuKey, entryKey, accountCode]);
}

/** The deal state of one SKU for the session's current entry and account; `undefined` (no deal)
 *  when the store holds another key's entries or none for this SKU. */
export function useMultibuyDealsEntry(sku: string): MultibuyDealsEntry | undefined {
  const currentKey = useCurrentPriceKey();
  return useMultibuyDealsStore((s) =>
    currentKey !== null && s.key === currentKey ? s.entries[sku] : undefined,
  );
}
